/**
 * Conversations API Hooks
 * Handles AI conversations data fetching with TanStack Query
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, buildQueryParams } from "@finitless/shared/lib";
import { isEndpointEnabled } from "@finitless/shared/config";

const ITEMS_PER_PAGE = 15;

/**
 * Hook to get all conversations with pagination and filtering
 * @param {Object} params - Query parameters
 * @param {string} params.params - Comma-separated fields to include
 * @param {number} params.page - Page number
 * @param {number} params.page_size - Items per page
 * @param {string} params.orderBy - Field to order by
 * @param {string} params.where - Filter conditions in JSON format
 */
export const useConversations = (params = {}) => {
  const _params = {
    ...params,
    params:
      "id,status,type,user(name,cellphone,email,country_phone_code),agent,messages_count,last_message_at,created_at,order(summary),usage,status_reason,business(name,logo)",
    orderBy: "-created_at",
    page_size: ITEMS_PER_PAGE,
  };
  const queryString = buildQueryParams(_params);

  return useQuery({
    queryKey: ["conversations", params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/ai/conversations${queryString ? `?${queryString}` : ""}`,
      );
      const conversations = response?.result || [];

      // Sort by last activity: use last_message_at only if conversation has messages, otherwise created_at
      conversations.sort((a, b) => {
        const dateA = new Date(
          a.messages_count > 0 ? a.last_message_at : a.created_at,
        );
        const dateB = new Date(
          b.messages_count > 0 ? b.last_message_at : b.created_at,
        );
        return dateB - dateA;
      });

      return {
        conversations,
        pagination: response?.pagination || {},
      };
    },
    enabled: isEndpointEnabled("ai", "conversations"),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("ai", "conversations"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to get a specific conversation by ID
 * @param {number} conversationId - Conversation ID
 * @param {Object} params - Query parameters
 * @param {string} params.params - Comma-separated fields to include
 */
export const useConversation = (conversationId, params = {}) => {
  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["conversation", conversationId, params],
    queryFn: async () => {
      if (!conversationId) return null;

      const response = await apiClient.get(
        `/ai/conversations/${conversationId}${
          queryString ? `?${queryString}` : ""
        }`,
      );
      return response?.result || null;
    },
    enabled: !!conversationId && isEndpointEnabled("ai", "conversations"),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount:
      !!conversationId && isEndpointEnabled("ai", "conversations"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to get messages from a specific conversation
 * @param {number} conversationId - Conversation ID
 * @param {Object} params - Query parameters
 * @param {string} params.params - Comma-separated fields to include
 * @param {number} params.page - Page number
 * @param {number} params.page_size - Items per page
 * @param {string} params.orderBy - Field to order by
 * @param {string} params.where - Filter conditions in JSON format
 */
export const useConversationMessages = (conversationId, params = {}) => {
  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["conversationMessages", conversationId, params],
    queryFn: async () => {
      if (!conversationId) return { messages: [], pagination: {} };

      const response = await apiClient.get(
        `/ai/conversations/${conversationId}/messages${
          queryString ? `?${queryString}` : ""
        }`,
      );
      return {
        messages: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    enabled: !!conversationId && isEndpointEnabled("ai", "conversations"),
    staleTime: 0, // 0 minute - messages change frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount:
      !!conversationId && isEndpointEnabled("ai", "conversations"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to get conversation statistics/metrics
 * This calculates metrics from the conversations data
 * @param {Object} params - Query parameters for filtering conversations
 */
export const useConversationStats = (params = {}) => {
  return useQuery({
    queryKey: ["conversationStats", params],
    queryFn: async () => {
      // Get all conversations to calculate stats
      const response = await apiClient.get("/ai/conversations", {
        params: {
          params: "id,status,type,created_at,last_message_at,messages_count",
          page_size: 15,
          orderBy: "-created_at",
          ...params,
        },
      });

      const conversations = response?.result || [];

      // Calculate statistics
      const stats = {
        total: conversations.length,
        active: conversations.filter((c) => c.status === "active").length,
        closed: conversations.filter((c) => c.status === "closed").length,
      };

      // Calculate AI success rate (resolved / total)
      if (stats.total > 0) {
        stats.aiSuccessRate =
          Math.round((stats.resolved / stats.total) * 100 * 10) / 10;
      }

      return stats;
    },
    enabled: isEndpointEnabled("ai", "conversations"),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("ai", "conversations"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to invalidate conversation queries
 * Useful for refreshing data after mutations
 */
export const useConversationInvalidation = () => {
  const queryClient = useQueryClient();

  const invalidateConversations = () => {
    queryClient.invalidateQueries(["conversations"]);
    queryClient.invalidateQueries(["conversationStats"]);
  };

  const invalidateConversation = (conversationId) => {
    queryClient.invalidateQueries(["conversation", conversationId]);
    queryClient.invalidateQueries(["conversationMessages", conversationId]);
  };

  const invalidateAll = () => {
    queryClient.invalidateQueries(["conversations"]);
    queryClient.invalidateQueries(["conversation"]);
    queryClient.invalidateQueries(["conversationMessages"]);
    queryClient.invalidateQueries(["conversationStats"]);
  };

  return {
    invalidateConversations,
    invalidateConversation,
    invalidateAll,
  };
};

export default {
  useConversations,
  useConversation,
  useConversationMessages,
  useConversationStats,
  useConversationInvalidation,
};
